import { LcgRandom } from './generator';
import {
  CAMPUS_ASSET_COUNTS,
  CAMPUS_DATASET_SEED,
  CAMPUS_DATASET_VERSION,
  CAMPUS_PROFILE_ID,
  CAMPUS_SCALE_PROFILE
} from './profile';
import type { CrocOpsDatabase } from '../local/database';
import type {
  Asset,
  Building,
  ChangeRequest,
  DatasetMetadata,
  Department,
  DeviceAssignment,
  Floor,
  Incident,
  MaintenanceTask,
  NetworkDevice,
  NetworkPath,
  ObservedDevice,
  PatchPanel,
  PatchPanelPort,
  Person,
  Room,
  Service,
  SwitchPort,
  Vendor,
  Vlan,
  WorkOrder,
  DataJack
} from '../domain';

const GENERATED_AT = '2026-07-24T12:00:00.000Z';

const BUILDING_NAMES = [
  ['BLD-NAC', 'North Academic Center', 'NA'],
  ['BLD-CLA', 'Cascade Library Annex', 'CL'],
  ['BLD-SP', 'Science Pavilion', 'SP'],
  ['BLD-SU', 'Student Union', 'SU'],
  ['BLD-ADM', 'Administration Building', 'AD'],
  ['BLD-ATH', 'Athletics Center', 'AT'],
  ['BLD-FAC', 'Fine Arts Center', 'FA'],
  ['BLD-RHA', 'Residence Hall A', 'RA'],
  ['BLD-CTC', 'Career and Technical Center', 'CT'],
  ['BLD-HSC', 'Health Sciences Center', 'HS'],
  ['BLD-EC', 'Early Learning Center', 'EL'],
  ['BLD-OPS', 'Campus Operations', 'OP'],
  ['BLD-WA', 'West Academic Hall', 'WA'],
  ['BLD-CC', 'Cedar Commons', 'CC'],
  ['BLD-RHB', 'Residence Hall B', 'RB'],
  ['BLD-PLT', 'Central Plant', 'CP']
] as const;

const DEPARTMENT_NAMES = [
  'Academic Advising', 'Information Technology', 'Human Resources',
  'Campus Security', 'Student Affairs', 'Facilities Management',
  'Business Office', 'Library Services', 'Health Sciences',
  'Career and Technical Education', 'Arts and Humanities', 'Mathematics',
  'Natural Sciences', 'Social Sciences', 'Athletics', 'Admissions',
  'Financial Aid', 'Registrar', 'Institutional Research',
  'Marketing and Communications', 'Accessibility Services',
  'Teaching and Learning', 'Executive Administration', 'Food Services'
];

type AssetCategoryKey = keyof typeof CAMPUS_ASSET_COUNTS;

const ASSET_CATEGORY_DETAILS: Record<AssetCategoryKey, {
  category: string;
  label: string;
  prefix: string;
}> = {
  desktops: { category: 'desktop-computer', label: 'Desktop computer', prefix: 'DTP' },
  issuedLaptops: { category: 'issued-laptop', label: 'College laptop', prefix: 'LTP' },
  labWorkstations: { category: 'lab-workstation', label: 'Lab workstation', prefix: 'LAB' },
  libraryComputers: { category: 'library-computer', label: 'Library computer', prefix: 'LIB' },
  kiosks: { category: 'public-kiosk', label: 'Public kiosk', prefix: 'KSK' },
  mobilePhones: { category: 'mobile-phone', label: 'College mobile phone', prefix: 'MOB' },
  tablets: { category: 'tablet', label: 'College tablet', prefix: 'TAB' },
  deskPhones: { category: 'desk-phone', label: 'Desk phone', prefix: 'TEL' },
  emergencyPhones: { category: 'emergency-phone', label: 'Emergency phone', prefix: 'EMR' },
  intercoms: { category: 'classroom-intercom', label: 'Classroom intercom', prefix: 'INT' },
  videoConference: { category: 'video-conference', label: 'Video conference system', prefix: 'VCR' },
  lecterns: { category: 'digital-lectern', label: 'Digital classroom lectern', prefix: 'LEC' },
  printers: { category: 'network-printer', label: 'Network printer', prefix: 'PRN' },
  specialtyPrinters: { category: 'specialty-printer', label: 'Specialty printer', prefix: 'SPR' },
  digitalSigns: { category: 'digital-sign', label: 'Digital sign', prefix: 'DSG' },
  displays: { category: 'classroom-display', label: 'Classroom display', prefix: 'DSP' },
  labDevices: { category: 'technical-training-device', label: 'Technical training device', prefix: 'TCH' },
  cameras: { category: 'security-camera', label: 'Security camera', prefix: 'CAM' },
  doorReaders: { category: 'door-reader', label: 'Electronic door reader', prefix: 'DOR' },
  alarmPanels: { category: 'alarm-panel', label: 'Alarm control panel', prefix: 'ALM' },
  buildingControllers: { category: 'building-controller', label: 'Building automation controller', prefix: 'BAS' },
  hvacSensors: { category: 'hvac-sensor', label: 'HVAC sensor', prefix: 'HVA' },
  lightingControllers: { category: 'lighting-controller', label: 'Lighting controller', prefix: 'LGT' },
  vending: { category: 'vending-machine', label: 'Connected vending machine', prefix: 'VND' },
  parking: { category: 'parking-device', label: 'Parking and permit device', prefix: 'PRK' },
  physicalServers: { category: 'physical-server', label: 'Physical server', prefix: 'SRV' },
  virtualServers: { category: 'virtual-server', label: 'Virtual server', prefix: 'VM' },
  storage: { category: 'storage-system', label: 'Storage system', prefix: 'STO' },
  backups: { category: 'backup-appliance', label: 'Backup appliance', prefix: 'BKP' },
  identity: { category: 'identity-server', label: 'Identity server', prefix: 'IDP' },
  logging: { category: 'security-logging', label: 'Security logging system', prefix: 'LOG' },
  coreRouters: { category: 'core-router', label: 'Core router', prefix: 'RTR' },
  edgeRouters: { category: 'edge-router', label: 'Internet edge router', prefix: 'EDG' },
  firewalls: { category: 'firewall', label: 'Firewall', prefix: 'FW' },
  coreSwitches: { category: 'core-switch', label: 'Core switch', prefix: 'CSW' },
  buildingSwitches: { category: 'building-switch', label: 'Building switch', prefix: 'SW' },
  aps: { category: 'wireless-access-point', label: 'Wireless access point', prefix: 'AP' },
  monitoring: { category: 'network-monitor', label: 'Network monitoring appliance', prefix: 'MON' },
  loadBalancers: { category: 'application-gateway', label: 'Application gateway', prefix: 'LB' },
  cellRepeaters: { category: 'cellular-repeater', label: 'Indoor cellular repeater', prefix: 'CEL' }
};

function padded(value: number, width = 4) {
  return value.toString().padStart(width, '0');
}

function makeBuildings(): Building[] {
  return BUILDING_NAMES.map(([id, name], index) => ({
    id,
    name,
    address: `${110 + index * 10} Cedar Reach Way`,
    description: 'Fictional Cedar Reach College campus building.'
  }));
}

function makeFloors(): Floor[] {
  return BUILDING_NAMES.flatMap(([buildingId, , code]) =>
    [1, 2, 3].map((level) => ({
      id: code === 'NA' ? `FL-NA-${level}` : `FL-${code}-${level}`,
      buildingId,
      name: `Level ${level}`,
      level
    }))
  );
}

function makeRooms(floors: Floor[]): Room[] {
  const rooms: Room[] = [
    { id: 'RM-NA-214', floorId: 'FL-NA-2', name: 'Room 214', capacity: 32, purpose: 'Classroom' },
    { id: 'RM-NA-MDF', floorId: 'FL-NA-1', name: 'MDF', purpose: 'Primary distribution room' }
  ];
  for (let index = 0; rooms.length < 820; index += 1) {
    const floor = floors[index % floors.length];
    const sequence = Math.floor(index / floors.length) + 1;
    rooms.push({
      id: `RM-${floor.id.replace('FL-', '')}-${padded(sequence, 3)}`,
      floorId: floor.id,
      name: `Room ${floor.level}${padded(sequence, 2)}`,
      capacity: 12 + (index % 37),
      purpose: index % 17 === 0 ? 'Network closet' : index % 7 === 0 ? 'Lab' : 'Instructional or office space'
    });
  }
  return rooms;
}

function makeDepartments(): Department[] {
  return DEPARTMENT_NAMES.map((name, index) => ({
    id: index === 0 ? 'DEPT-AA' : index === 1 ? 'DEPT-IT' : `DEPT-${padded(index + 1, 2)}`,
    name
  }));
}

function makePersons(rng: LcgRandom, departments: Department[]): Person[] {
  const people: Person[] = [
    {
      id: 'USR-PRIYA-NAIR', firstName: 'Priya', lastName: 'Nair',
      email: 'pnair@cedarreach.edu', departmentId: 'DEPT-AA', title: 'Academic Advisor'
    },
    {
      id: 'USR-MAYA', firstName: 'Maya', lastName: 'Chen',
      email: 'mchen@cedarreach.edu', departmentId: 'DEPT-IT', title: 'Network Engineer'
    }
  ];
  const firstNames = ['Avery', 'Cameron', 'Devin', 'Elena', 'James', 'Jordan', 'Kai', 'Linda', 'Mary', 'Morgan', 'Noah', 'Patricia', 'Riley', 'Robert', 'Samira', 'Taylor'];
  const lastNames = ['Brown', 'Davis', 'Garcia', 'Johnson', 'Jones', 'Kim', 'Martinez', 'Miller', 'Nguyen', 'Patel', 'Rivera', 'Smith', 'Thompson', 'Williams'];
  for (let index = 3; people.length < CAMPUS_SCALE_PROFILE.employees; index += 1) {
    const firstName = rng.pick(firstNames);
    const lastName = rng.pick(lastNames);
    people.push({
      id: `USR-${padded(index, 4)}`,
      firstName,
      lastName,
      email: `employee${padded(index, 4)}@cedarreach.edu`,
      departmentId: departments[index % departments.length].id,
      title: index % 9 === 0 ? 'Department Lead' : 'College Employee'
    });
  }
  return people;
}

function makeAssets(rng: LcgRandom, rooms: Room[]): Asset[] {
  const assets: Asset[] = [];
  for (const [key, count] of Object.entries(CAMPUS_ASSET_COUNTS) as [AssetCategoryKey, number][]) {
    const detail = ASSET_CATEGORY_DETAILS[key];
    for (let index = 1; index <= count; index += 1) {
      const isAnchorDeskPhone = key === 'deskPhones' && index === 1;
      const isAnchorMobile = key === 'mobilePhones' && index === 1;
      const generatedIndex = key === 'deskPhones' && index >= 318 ? index + 1 : index;
      const id = isAnchorDeskPhone
        ? 'TEL-0318'
        : isAnchorMobile
          ? 'MOB-0418'
          : `${detail.prefix}-${padded(generatedIndex)}`;
      const healthRoll = rng.next();
      const health = isAnchorDeskPhone
        ? 'offline'
        : healthRoll < 0.008 ? 'degraded' : healthRoll < 0.02 ? 'unknown' : 'healthy';
      assets.push({
        id,
        assetTypeId: `TYPE-${detail.category.toUpperCase()}`,
        name: isAnchorDeskPhone ? 'Cisco 8841' : isAnchorMobile ? 'iPhone 14' : `${detail.label} ${padded(index)}`,
        assetTag: `TAG-${id}`,
        serialNumber: `CR-${detail.prefix}-${padded(index, 6)}`,
        state: key === 'deskPhones' || key === 'mobilePhones' || key === 'issuedLaptops' ? 'assigned' : 'available',
        health,
        locationId: rooms[(index * 17 + assets.length) % rooms.length].id,
        category: detail.category,
        ownership: 'college'
      });
    }
  }
  return assets;
}

function makeAssignments(assets: Asset[], people: Person[]): DeviceAssignment[] {
  const assignable = assets.filter((asset) =>
    ['desk-phone', 'mobile-phone', 'issued-laptop'].includes(asset.category ?? '')
  );
  return assignable.map((asset, index) => ({
    id: asset.id === 'TEL-0318' ? 'ASN-1182' : `ASN-${padded(index + 1, 5)}`,
    assetId: asset.id,
    personId: asset.id === 'TEL-0318' ? 'USR-PRIYA-NAIR' : people[index % people.length].id,
    assignedAt: `2026-01-${padded((index % 27) + 1, 2)}T09:00:00Z`
  }));
}

function makeObservedDevices(buildings: Building[]): ObservedDevice[] {
  const classes = ['phone', 'phone', 'phone', 'laptop', 'tablet'] as const;
  return Array.from({ length: CAMPUS_SCALE_PROFILE.observedDevices }, (_, index) => ({
    id: `OBS-${padded(index + 1, 5)}`,
    pseudonymousId: `campus-observation-${padded(index + 1, 5)}`,
    deviceClass: classes[index % classes.length],
    firstSeenAt: `2026-07-24T${padded(7 + (index % 10), 2)}:00:00Z`,
    lastSeenAt: `2026-07-24T${padded(8 + (index % 10), 2)}:45:00Z`,
    buildingId: buildings[index % buildings.length].id,
    connectionState: index % 131 === 0 ? 'degraded' : 'healthy',
    ownership: 'personal-observed'
  }));
}

function makeInfrastructure(rooms: Room[]) {
  const dataJacks: DataJack[] = [];
  const patchPanels: PatchPanel[] = [];
  const patchPanelPorts: PatchPanelPort[] = [];
  const networkDevices: NetworkDevice[] = [];
  const switchPorts: SwitchPort[] = [];
  const networkPaths: NetworkPath[] = [];

  for (let index = 1; index <= 80; index += 1) {
    patchPanels.push({
      id: index === 1 ? 'NA-MDF-PP03' : `PP-${padded(index, 3)}`,
      name: index === 1 ? 'NA-MDF-PP03' : `Campus patch panel ${padded(index, 3)}`,
      roomId: index === 1 ? 'RM-NA-MDF' : rooms[(index * 11) % rooms.length].id,
      model: 'CommScope 48-port'
    });
  }

  const networkAssetCategories = (Object.entries(CAMPUS_ASSET_COUNTS) as [AssetCategoryKey, number][])
    .filter(([key]) => ['coreRouters', 'edgeRouters', 'firewalls', 'coreSwitches', 'buildingSwitches', 'aps', 'monitoring', 'loadBalancers', 'cellRepeaters'].includes(key));
  let deviceIndex = 0;
  for (const [key, count] of networkAssetCategories) {
    const detail = ASSET_CATEGORY_DETAILS[key];
    for (let index = 1; index <= count; index += 1) {
      deviceIndex += 1;
      const isAnchor = key === 'buildingSwitches' && index === 1;
      networkDevices.push({
        id: isAnchor ? 'CR-SW-04' : `NET-${detail.prefix}-${padded(index)}`,
        name: isAnchor ? 'CR-SW-04' : `${detail.label} ${padded(index)}`,
        deviceRole: detail.category,
        deviceType: key === 'aps' ? 'wireless' : key.toLocaleLowerCase().includes('router') ? 'router' : 'switch',
        roomId: isAnchor ? 'RM-NA-MDF' : rooms[(deviceIndex * 7) % rooms.length].id,
        ipAddress: `10.${1 + (deviceIndex % 220)}.${Math.floor(deviceIndex / 220)}.${1 + (deviceIndex % 250)}`
      });
    }
  }

  for (let index = 1; index <= CAMPUS_SCALE_PROFILE.installedJacks; index += 1) {
    const isAnchor = index === 1;
    const jackId = isAnchor ? 'NA-214-A1' : `JACK-${padded(index, 5)}`;
    const panel = patchPanels[(index - 1) % patchPanels.length];
    const panelPortId = isAnchor ? 'NA-MDF-PP03:18' : `${panel.id}:J${padded(index, 5)}`;
    const switchDevice = isAnchor ? networkDevices.find((device) => device.id === 'CR-SW-04')! : networkDevices[(index * 13) % networkDevices.length];
    const switchPortId = isAnchor ? 'CR-SW-04:G1/0/18' : `${switchDevice.id}:J${padded(index, 5)}`;
    const room = isAnchor ? rooms.find((item) => item.id === 'RM-NA-214')! : rooms[(index * 19) % rooms.length];
    const status = index <= CAMPUS_SCALE_PROFILE.activeJacks ? 'active' : index <= 3400 ? 'unused' : 'unknown';
    dataJacks.push({
      id: jackId,
      roomId: room.id,
      name: jackId,
      label: isAnchor ? 'Jack A1' : `Jack ${padded(index, 5)}`,
      status,
      description: isAnchor ? 'Wall jack in 214' : `Fictional campus jack; ${status} inventory state.`
    });
    patchPanelPorts.push({
      id: panelPortId,
      panelId: panel.id,
      portNumber: isAnchor ? '18' : `${1 + ((index - 1) % 48)}`,
      label: isAnchor ? '18' : `${1 + ((index - 1) % 48)}`,
      connectedToId: switchPortId
    });
    switchPorts.push({
      id: switchPortId,
      deviceId: switchDevice.id,
      name: isAnchor ? 'Gi1/0/18' : `Port ${1 + ((index - 1) % 48)}`,
      portNumber: isAnchor ? '18' : `${1 + ((index - 1) % 48)}`,
      status: isAnchor ? 'degraded' : status === 'active' ? 'healthy' : 'unknown',
      vlanId: index % 4 === 0 ? 'VLAN-120' : 'VLAN-110'
    });
    networkPaths.push(
      { id: isAnchor ? 'PATH-1' : `PATH-J-${padded(index, 5)}-1`, sourceId: room.id, targetId: jackId, pathStatus: isAnchor ? 'degraded' : status === 'active' ? 'healthy' : 'unknown' },
      { id: isAnchor ? 'PATH-2' : `PATH-J-${padded(index, 5)}-2`, sourceId: jackId, targetId: panelPortId, pathStatus: isAnchor ? 'degraded' : status === 'active' ? 'healthy' : 'unknown' },
      { id: isAnchor ? 'PATH-3' : `PATH-J-${padded(index, 5)}-3`, sourceId: panelPortId, targetId: switchPortId, pathStatus: isAnchor ? 'degraded' : status === 'active' ? 'healthy' : 'unknown' }
    );
  }

  networkPaths.push(
    { id: 'PATH-4', sourceId: 'CR-SW-04:G1/0/18', targetId: 'VLAN-120', pathStatus: 'degraded' },
    { id: 'PATH-5', sourceId: 'VLAN-120', targetId: 'TEL-0318', pathStatus: 'degraded' },
    { id: 'PATH-6', sourceId: 'TEL-0318', targetId: 'USR-PRIYA-NAIR', pathStatus: 'degraded' },
    { id: 'PATH-7', sourceId: 'USR-PRIYA-NAIR', targetId: 'svc-staff-wifi', pathStatus: 'degraded' },
    { id: 'PATH-8', sourceId: 'svc-staff-wifi', targetId: 'INC-2048', pathStatus: 'degraded' }
  );
  return { dataJacks, patchPanels, patchPanelPorts, networkDevices, switchPorts, networkPaths };
}

function makeOperationsRecords() {
  const vendors: Vendor[] = [
    { id: 'Cascade Fiber Cooperative', name: 'Cascade Fiber Cooperative', status: 'healthy' },
    ...Array.from({ length: 5 }, (_, index) => ({
      id: `VENDOR-${padded(index + 2, 2)}`,
      name: `Campus Technology Partner ${index + 2}`,
      status: index === 3 ? 'review' : 'healthy'
    }))
  ];
  const services: Service[] = [
    { id: 'svc-staff-wifi', vendorId: 'Cascade Fiber Cooperative', name: 'Staff Wireless', status: 'disrupted' },
    ...Array.from({ length: 23 }, (_, index) => ({
      id: `SVC-${padded(index + 2, 3)}`,
      vendorId: vendors[(index + 1) % vendors.length].id,
      name: `Campus service ${index + 2}`,
      status: index % 11 === 0 ? 'degraded' : 'healthy'
    }))
  ];
  const incidents: Incident[] = [
    {
      id: 'INC-2048',
      incidentNumber: 'INC-2048',
      title: 'North Academic Building Network Degradation',
      description: '183 people are experiencing unstable wireless and VoIP registration.',
      state: 'investigating',
      severity: 'SEV-2',
      createdAt: '2026-07-23T10:23:00Z',
      updatedAt: '2026-07-23T10:23:00Z'
    },
    ...Array.from({ length: 17 }, (_, index) => ({
      id: `INC-${padded(2050 + index, 4)}`,
      incidentNumber: `INC-${padded(2050 + index, 4)}`,
      title: `Campus operations event ${index + 2}`,
      description: 'Fictional event retained for queue and reporting scale.',
      state: index % 3 === 0 ? 'resolved' as const : 'monitoring' as const,
      severity: index % 5 === 0 ? 'SEV-2' as const : 'SEV-3' as const,
      createdAt: `2026-07-${padded(1 + (index % 22), 2)}T08:00:00Z`,
      updatedAt: `2026-07-${padded(1 + (index % 22), 2)}T09:00:00Z`
    }))
  ];
  const maintenanceTasks: MaintenanceTask[] = [
    { id: 'MNT-882', maintenancePlanId: 'PLAN-1', isRequired: true, description: 'Check NA-MDF patch panels' },
    ...Array.from({ length: 44 }, (_, index) => ({
      id: `MNT-${padded(883 + index, 3)}`,
      maintenancePlanId: `PLAN-${padded(2 + (index % 12), 2)}`,
      isRequired: index % 5 !== 0,
      description: `Campus preventive maintenance task ${index + 2}`
    }))
  ];
  const workOrders: WorkOrder[] = [
    {
      id: 'WO-882', state: 'in-progress', title: 'Closet Inspection',
      description: 'Inspect NA-MDF', priority: 'medium',
      createdAt: '2026-07-23T09:00:00Z', updatedAt: '2026-07-23T09:00:00Z'
    }
  ];
  const changeRequests: ChangeRequest[] = [{
    id: 'CHG-9012',
    title: 'Update trunk allowances on CR-SW-04',
    description: 'Trunk modifications',
    status: 'implemented',
    requesterId: 'USR-MAYA',
    createdAt: '2026-07-23T09:00:00Z'
  }];
  return { vendors, services, incidents, maintenanceTasks, workOrders, changeRequests };
}

export function buildCampusCatalog(seed: number = CAMPUS_DATASET_SEED) {
  const rng = new LcgRandom(seed);
  const buildings = makeBuildings();
  const floors = makeFloors();
  const rooms = makeRooms(floors);
  const departments = makeDepartments();
  const persons = makePersons(rng, departments);
  const assets = makeAssets(rng, rooms);
  const deviceAssignments = makeAssignments(assets, persons);
  const observedDevices = makeObservedDevices(buildings);
  return {
    assets,
    buildings,
    departments,
    deviceAssignments,
    floors,
    observedDevices,
    persons,
    rooms
  };
}

export function buildCampusDataset(seed: number = CAMPUS_DATASET_SEED) {
  const {
    assets,
    buildings,
    departments,
    deviceAssignments,
    floors,
    observedDevices,
    persons,
    rooms
  } = buildCampusCatalog(seed);
  const infrastructure = makeInfrastructure(rooms);
  const operations = makeOperationsRecords();
  const vlans: Vlan[] = [
    { id: 'VLAN-110', vid: 110, name: 'Campus data', status: 'active' },
    { id: 'VLAN-120', vid: 120, name: 'Voice', status: 'active' }
  ];
  const metadata: DatasetMetadata = {
    id: 'canonical',
    profileId: CAMPUS_PROFILE_ID,
    version: CAMPUS_DATASET_VERSION,
    seed,
    generatedAt: GENERATED_AT,
    managedAssetCount: assets.length,
    observedDeviceCount: observedDevices.length,
    identifiableDeviceCount: assets.length + observedDevices.length
  };

  return {
    assets,
    buildings,
    departments,
    deviceAssignments,
    floors,
    infrastructure,
    metadata,
    observedDevices,
    operations,
    persons,
    rooms,
    vlans
  };
}

export async function populateDatabase(
  db: CrocOpsDatabase,
  seed: number = CAMPUS_DATASET_SEED
) {
  const {
    assets,
    buildings,
    departments,
    deviceAssignments,
    floors,
    infrastructure,
    metadata,
    observedDevices,
    operations,
    persons,
    rooms,
    vlans
  } = buildCampusDataset(seed);
  const persistedAssets = assets.filter((asset) => ["TEL-0318", "MOB-0418"].includes(asset.id));
  const persistedAssignments = deviceAssignments.filter((assignment) => assignment.id === "ASN-1182");
  const persistedObservedDevices = observedDevices.slice(0, 250);
  const persistedPatchPorts = infrastructure.patchPanelPorts.filter((port) => port.id === "NA-MDF-PP03:18");
  const persistedSwitchPorts = infrastructure.switchPorts.filter((port) => port.id === "CR-SW-04:G1/0/18");
  const persistedPaths = infrastructure.networkPaths.filter((path) =>
    ["PATH-1", "PATH-2", "PATH-3", "PATH-4", "PATH-5", "PATH-6", "PATH-7", "PATH-8"].includes(path.id)
  );

  await db.transaction('rw', db.tables, async () => {
    for (const table of db.tables) await table.clear();
    await db.buildings.bulkAdd(buildings);
    await db.floors.bulkAdd(floors);
    await db.rooms.bulkAdd(rooms);
    await db.departments.bulkAdd(departments);
    await db.persons.bulkAdd(persons);
    await db.assets.bulkAdd(persistedAssets);
    await db.deviceAssignments.bulkAdd(persistedAssignments);
    await db.observedDevices.bulkAdd(persistedObservedDevices);
    await db.dataJacks.bulkAdd(infrastructure.dataJacks);
    await db.patchPanels.bulkAdd(infrastructure.patchPanels);
    await db.patchPanelPorts.bulkAdd(persistedPatchPorts);
    await db.networkDevices.bulkAdd(infrastructure.networkDevices);
    await db.switchPorts.bulkAdd(persistedSwitchPorts);
    await db.networkPaths.bulkAdd(persistedPaths);
    await db.vlans.bulkAdd(vlans);
    await db.incidents.bulkAdd(operations.incidents);
    await db.changeRequests.bulkAdd(operations.changeRequests);
    await db.workOrders.bulkAdd(operations.workOrders);
    await db.maintenanceTasks.bulkAdd(operations.maintenanceTasks);
    await db.vendors.bulkAdd(operations.vendors);
    await db.services.bulkAdd(operations.services);
    await db.datasetMetadata.put(metadata);
  });
}
